/**
 * Per-user shell preferences (M2 · 86d44z1p4, owner decision 2 of 2026-08-26).
 *
 * "Remember per user": the module panel is OPEN on a user's first visit and thereafter whatever
 * they last chose. Per-user, not per-browser — the auth store persists to `localStorage`, which is
 * shared across tabs and survives a tab closing, so a single global key would hand the next person
 * to sign in on this machine someone else's layout.
 *
 * ⛔ The orphaned `sidebar-pinned` key is RETIRED, not reused: it is a global boolean with the
 * opposite default, so inheriting it would open the panel for people who had deliberately
 * collapsed the old sidebar (and vice versa). It is simply left to rot; nothing reads it after M7.
 */

const PANEL_OPEN_PREFIX = 'dop.shell.panelOpen.';

/** Owner decision: first visit shows the panel open. */
export const PANEL_OPEN_DEFAULT = true;

function keyFor(userId: number | null): string | null {
  return userId === null ? null : `${PANEL_OPEN_PREFIX}${userId}`;
}

/**
 * Read this user's remembered panel state, falling back to the default.
 *
 * Every access is guarded: `localStorage` THROWS (not returns null) in some privacy modes and
 * embedded webviews, and an unguarded read here would white-screen the whole shell.
 */
export function readPanelOpen(userId: number | null): boolean {
  const key = keyFor(userId);
  if (key === null) return PANEL_OPEN_DEFAULT;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return PANEL_OPEN_DEFAULT;
    return raw === 'true';
  } catch {
    return PANEL_OPEN_DEFAULT;
  }
}

/** Remember this user's panel state. Silently a no-op when storage is unavailable. */
export function writePanelOpen(userId: number | null, open: boolean): void {
  const key = keyFor(userId);
  if (key === null) return;
  try {
    localStorage.setItem(key, String(open));
  } catch {
    /* Storage unavailable — the preference just does not survive the session. */
  }
}
